import type { IReactNativeFileUri } from "./chat";
import type { TEntityId } from "./entity-id";

export enum EKnowledgeProcessingStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
}

export enum EKnowledgeSource {
  PROJECT = "project",
  SYNCATOR = "syncator",
  ASSISTANT = "assistant",
  FOLDER = "folder",
}

export interface IKnowledgeFile {
  uuid: string;
  name: string;
  mimeType: string;
  fileSize: number;
  processingStatus: EKnowledgeProcessingStatus;
  processingError: string | null;
  chunkCount: number;
  source: EKnowledgeSource;
  isPublic: boolean;
  isOwner: boolean;
  shareCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface IKnowledgeQueryResult {
  content: string;
  score: number;
  fileName: string;
  fileUuid: string;
}

export interface IKnowledgeQueryResponse {
  results: IKnowledgeQueryResult[];
}

export interface IKnowledgeDownloadResponse {
  url: string;
  fileName: string;
  mimeType: string;
}

export interface IKnowledgeAnalytics {
  totalFiles: number;
  totalStorageBytes: number;
  totalChunks: number;
  byStatus: Record<string, number>;
  bySource: Record<string, number>;
}

export interface IKnowledgeShare {
  id: TEntityId;
  sourceProjectId: TEntityId;
  targetProjectId: TEntityId;
  targetProject: {
    id: TEntityId;
    title: string;
    uuid: string;
  } | null;
  createdAt: string;
}

export type TSharedWithMeShareType = "file_share" | "public";

export interface IKnowledgeFileShare {
  id: TEntityId;
  targetProject: {
    title: string;
    uuid: string;
  } | null;
  targetUser: {
    uuid: string;
    firstName: string;
    lastName: string;
    email: string | null;
  } | null;
  createdAt: string;
}

export interface ISharedWithMeFile {
  uuid: string;
  name: string;
  mimeType: string;
  fileSize: number;
  processingStatus: EKnowledgeProcessingStatus;
  chunkCount: number;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  shareType: TSharedWithMeShareType;
  sourceProject: {
    uuid: string;
    title: string;
  } | null;
  sharedBy: {
    firstName: string;
    lastName: string;
  } | null;
}

export interface ICreateFileShareRequest {
  fileUuid: string;
  targetProjectUuid: string;
  targetUserUuids?: string[];
}

export interface IUploadKnowledgeFileParams {
  // NOTE: React Native has no `File` — its `FormData` takes a
  // `{ uri, type, name }` descriptor instead, so the mobile app passes
  // `IReactNativeFileUri` while web passes a real `File`.
  file: File | IReactNativeFileUri;
  // NOTE: Optional. Abort the in-flight POST. axios raises a `CanceledError`
  // which `useUploadKnowledgeFile` catches to drop the row inline.
  signal?: AbortSignal;
  // NOTE: Optional. Receives 0–100 percent progress as the body uploads.
  // Backed by axios `onUploadProgress` (XHR `progress` events).
  onProgress?: (percent: number) => void;
}

// NOTE: Folder-scoped knowledge file (Claude Projects-style). The folder
// endpoints return a subset of IKnowledgeFile — no sharing/visibility, since
// folder files are private to the folder owner.
export interface IFolderKnowledgeFile {
  uuid: string;
  name: string;
  mimeType: string;
  fileSize: number;
  processingStatus: EKnowledgeProcessingStatus;
  processingError: string | null;
  chunkCount: number;
  source: EKnowledgeSource;
  isOwner: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IUploadFolderKnowledgeFileParams extends IUploadKnowledgeFileParams {
  folderUuid: string;
}

export interface IDeleteFolderKnowledgeFileParams {
  folderUuid: string;
  uuid: string;
}

export interface ISaveGeneratedFileToFolderParams {
  folderUuid: string;
  fileUuid: string;
}
